import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { invalidateClaimWizardMetadata } from '@/admin/lib/wizard-metadata-cache'
import type { PaginatedResponse } from '@/shared/types/api'

// ── Types ────────────────────────────────────────────────

export interface ClaimAttribute {
  uuid: string
  field_type: string
  editable: boolean
  code_name: string
  display_name: string
  description: string
  required: boolean
  configuration_data: Record<string, unknown>
  order: number
  visibility: 'PUBLIC' | 'PARTNER' | 'INTERNAL'
}

export interface ClaimSchemaRow {
  uuid: string
  name: string
  description: string
  attributes: ClaimAttribute[]
  created_at: string
  updated_at: string
}

// ── Hooks ────────────────────────────────────────────────

export function useClaimSchemas(enabled = true) {
  return useQuery<ClaimSchemaRow[]>({
    queryKey: ['claim-schemas'],
    queryFn: () =>
      api
        .get<PaginatedResponse<ClaimSchemaRow>>('/claims/claim-schemas/', {
          params: { page_size: 200 },
        })
        .then((r) => r.data.results),
    enabled,
  })
}

export function useClaimSchema(uuid: string | null) {
  return useQuery<ClaimSchemaRow>({
    queryKey: ['claim-schema', uuid],
    queryFn: () => api.get(`/claims/claim-schemas/${uuid}/`).then((r) => r.data),
    enabled: !!uuid,
  })
}

export function useCreateClaimSchema() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      api.post('/claims/claim-schemas/', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['claim-schemas'] })
      invalidateClaimWizardMetadata(qc)
    },
  })
}

export function useUpdateClaimSchema() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, data }: { uuid: string; data: Partial<ClaimSchemaRow> }) =>
      api.put(`/claims/claim-schemas/${uuid}/`, data).then((r) => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['claim-schemas'] })
      qc.invalidateQueries({ queryKey: ['claim-schema', vars.uuid] })
      invalidateClaimWizardMetadata(qc)
    },
  })
}

export function useDeleteClaimSchema() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) => api.delete(`/claims/claim-schemas/${uuid}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['claim-schemas'] })
      invalidateClaimWizardMetadata(qc)
    },
  })
}

// ── Attribute Hooks ──────────────────────────────────────

export function useCreateClaimAttribute() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      schemaUuid,
      data,
    }: {
      schemaUuid: string
      data: Partial<ClaimAttribute>
    }) => api.post(`/claims/claim-schemas/${schemaUuid}/attributes/`, data).then((r) => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['claim-schemas'] })
      qc.invalidateQueries({ queryKey: ['claim-schema', vars.schemaUuid] })
      invalidateClaimWizardMetadata(qc)
    },
  })
}

export function useUpdateClaimAttribute() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      schemaUuid,
      attrUuid,
      data,
    }: {
      schemaUuid: string
      attrUuid: string
      data: Partial<ClaimAttribute>
    }) =>
      api
        .put(`/claims/claim-schemas/${schemaUuid}/attributes/${attrUuid}/`, data)
        .then((r) => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['claim-schemas'] })
      qc.invalidateQueries({ queryKey: ['claim-schema', vars.schemaUuid] })
      invalidateClaimWizardMetadata(qc)
    },
  })
}

export function useDeleteClaimAttribute() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      schemaUuid,
      attrUuid,
      force,
    }: {
      schemaUuid: string
      attrUuid: string
      force?: boolean
    }) =>
      api.delete(`/claims/claim-schemas/${schemaUuid}/attributes/${attrUuid}/`, {
        params: force ? { force: 'true' } : undefined,
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['claim-schemas'] })
      qc.invalidateQueries({ queryKey: ['claim-schema', vars.schemaUuid] })
      invalidateClaimWizardMetadata(qc)
    },
  })
}
